import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  changeRequestStatus,
  uploadChatMedia,
  addRequestComment,
} from '@/lib/services/request';
import { QueryKeys } from '@/utils/constants/QueryKey';
import { ToastType } from '@/utils/constants';
import { useToast } from '../Toast';
import { handleError } from '@/utils/common';
import { acceptTermsAndPrivacyPolicy } from '@/lib/services/users';
import { RequestStatus } from '@/types/request';

interface ChangeStatusPayload {
  id: string;
  newStatus: RequestStatus;
  transferReason?: string;
  newOrganizationId?: string;
  newServiceId?: string;
  comments?: string;
}

export const useChangeRequestStatus = ({
  onSuccess,
  onError,
}: { onSuccess?: () => void; onError?: (error: any) => void } = {}) => {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  const mutation = useMutation({
    mutationFn: async (data: ChangeStatusPayload) => {
      // Build the payload based on newStatus condition
      let payload: any = { status: data.newStatus };

      if (data.newStatus === RequestStatus.Referred) {
        payload = {
          ...payload,
          transferReason: data.transferReason,
          newOrganizationId: data.newOrganizationId,
          newServiceId: data.newServiceId,
        };
      }

      if (
        [
          RequestStatus.InProgress,
          RequestStatus.Pending,
          RequestStatus.Resolved,
        ].includes(data.newStatus)
      ) {
        payload = {
          ...payload,
          comments: data.comments,
        };
      }

      return await changeRequestStatus(data.id, payload);
    },
    onSuccess: () => {
      toggleToast({
        type: ToastType.Success,
        message: 'Status changed successfully',
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_REQUESTS] });
      onSuccess?.();
    },
    onError: (error) => {
      const err = handleError(error);
      toggleToast({ type: ToastType.Error, message: err.message });
      onError?.(err);
    },
  });
  return mutation;
};

export const useChatMediaUpload = ({
  onProgress,
}: {
  onProgress: (percentage: number, isUploading: boolean) => void;
}) => {
  return useMutation({
    mutationFn: (formData: FormData) => uploadChatMedia(formData, onProgress),
  });
};

export const useAcceptTermsAndPrivacy = () => {
  return useMutation({
    mutationFn: acceptTermsAndPrivacyPolicy,
  });
};

export const useAddRequestComment = ({
  onSuccess,
  onError,
}: { onSuccess?: () => void; onError?: (error: any) => void } = {}) => {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({
      requestId,
      comment,
    }: {
      requestId: string;
      comment: string;
    }) => {
      return await addRequestComment(requestId, comment);
    },
    onSuccess: (_data, variables) => {
      toggleToast({
        type: ToastType.Success,
        message: 'Comment added successfully',
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_REQUEST_COMMENTS, variables.requestId],
      });
      onSuccess?.();
    },
    onError: (error) => {
      const err = handleError(error);
      toggleToast({ type: ToastType.Error, message: err.message });
      onError?.(err);
    },
  });
  return mutation;
};
